import React, { useMemo } from "react";
import "./vehicleSummaryCard.css";

function gradeToColor(grade) {
  const g = String(grade ?? "").trim().toUpperCase();
  if (g.startsWith("A") || g.startsWith("B")) return { border: "#22c55e", glow: "rgba(34,197,94,0.5)", text: "#22c55e" };
  if (g.startsWith("C")) return { border: "#eab308", glow: "rgba(234,179,8,0.45)", text: "#eab308" };
  if (g.startsWith("D") || g === "F") return { border: "#ef4444", glow: "rgba(239,68,68,0.5)", text: "#ef4444" };
  return { border: "#94a3b8", glow: "rgba(148,163,184,0.4)", text: "#94a3b8" };
}

export default function VehicleSummaryCard({ vehicle, data }) {
  // Use data prop if provided (from API), otherwise fall back to vehicle prop or null
  const vehicleInfo = data?.vehicle_info || {};
  const pricingInfo = data?.pricing_info || {};
  const searchMetadata = data?.search_metadata || {};
  const metrics = data?.market_analysis?.market_metrics || {};
  // API: market_analysis.ai_market_insights.overall_grade is { overall_score, overall_grade } — extract letter
  const rawOverall =
    data?.market_analysis?.ai_market_insights?.overall_grade ??
    data?.ai_market_insights?.overall_grade;
  const overallGrade =
    rawOverall && typeof rawOverall === "object" && "overall_grade" in rawOverall
      ? rawOverall.overall_grade
      : typeof rawOverall === "string"
        ? rawOverall
        : null;
  const gradeColors = gradeToColor(overallGrade);
  
  // Use vehicle prop if provided (from selected comp), otherwise use hooptie vehicle data
  const hooptiePrice = pricingInfo.marketcheck_price || searchMetadata.asking_price || 0;
  const hooptieMileage = searchMetadata.mileage || 0;
  const vehicleTitle = vehicle 
    ? vehicle.title 
    : `${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model} ${vehicleInfo.trim}`;
  const vehicleVersion = vehicle?.version ?? vehicleInfo.version ?? '';
  const vehicleDrivetrain = vehicle?.drivetrain ?? vehicleInfo.drivetrain ?? vehicleInfo.drivetrain_type ?? '';
  const vehicleTransmission = vehicle?.transmission ?? vehicleInfo.transmission ?? vehicleInfo.transmission_type ?? '';
  const displayPrice = vehicle ? (vehicle.price != null ? vehicle.price : null) : hooptiePrice;
  const displayMileage = vehicle ? (vehicle.mileage != null ? vehicle.mileage : null) : hooptieMileage;
  const displayVin = vehicle?.vin || vehicleInfo.vin;
  const displayDom = vehicle?.dom || 0;
  const soldDate = vehicle?.soldDate;

  const activeListings = data?.active_listings || data?.market_analysis?.active_listings || [];
  const recentSales = data?.market_analysis?.recent_sales || [];

  const { avgDom, avgPrice, priceBand } = useMemo(() => {
    const soldDoms = recentSales
      .map((s) => s.dom ?? s.dom_active)
      .filter((n) => n != null && !Number.isNaN(Number(n)));
    const computedAvgDom =
      soldDoms.length > 0
        ? Math.round(soldDoms.reduce((a, b) => a + Number(b), 0) / soldDoms.length)
        : null;

    const activePrices = activeListings
      .map((l) => l.asking_price ?? l.price)
      .filter((n) => n != null && !Number.isNaN(Number(n)) && Number(n) > 0);
    const computedAvgPrice =
      activePrices.length > 0
        ? Math.round(activePrices.reduce((a, b) => a + Number(b), 0) / activePrices.length)
        : null;

    const pricesForBand = activeListings
      .map((l) => l.asking_price ?? l.price)
      .filter((n) => n != null && !Number.isNaN(Number(n)) && Number(n) > 0);
    const minP = pricesForBand.length > 0 ? Math.min(...pricesForBand) : null;
    const maxP = pricesForBand.length > 0 ? Math.max(...pricesForBand) : null;
    const bandStr =
      minP != null && maxP != null
        ? `$${(minP / 1000).toFixed(1)}k - $${(maxP / 1000).toFixed(1)}k`
        : null;

    return {
      avgDom: computedAvgDom ?? metrics?.avg_days_on_market ?? 0,
      avgPrice:
        computedAvgPrice ??
        (metrics?.avg_active_price != null ? Math.round(metrics.avg_active_price) : metrics?.market_avg_price != null ? Math.round(metrics.market_avg_price) : 0),
      priceBand:
        bandStr ??
        (metrics?.price_range_active?.min != null && metrics?.price_range_active?.max != null
          ? `$${(metrics.price_range_active.min / 1000).toFixed(1)}k - $${(metrics.price_range_active.max / 1000).toFixed(1)}k`
          : "—"),
    };
  }, [activeListings, recentSales, metrics?.avg_days_on_market, metrics?.avg_active_price, metrics?.market_avg_price, metrics?.price_range_active]);
  
  // Format sold date
  const formatSoldDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="vehicle-summary-card">
      <div className="vs-header">
        <h3 className="vs-title">{vehicleTitle}</h3>
        {(vehicleVersion || vehicleDrivetrain || vehicleTransmission) && (
          <div className="vs-version vs-version-vertical">
            {vehicleVersion && <div className="vs-version-line">Version: {vehicleVersion}</div>}
            {vehicleDrivetrain && <div className="vs-version-line">Drivetrain: {vehicleDrivetrain}</div>}
            {vehicleTransmission && <div className="vs-version-line">Transmission: {vehicleTransmission}</div>}
          </div>
        )}
      </div>

      <div className="vs-image-container vs-grade-container">
        <div
          className="vs-grade-circle"
          style={{
            width: 140,
            height: 140,
            borderRadius: "50%",
            border: `4px solid ${gradeColors.border}`,
            boxShadow: `0 0 24px ${gradeColors.glow}`,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: gradeColors.text, fontSize: 64, fontWeight: 800, lineHeight: 1 }}>
            {typeof overallGrade === "string" ? overallGrade : "—"}
          </span>
        </div>
      </div>

      <div className="vs-details">
        <div className="vs-price-row">
          <div className="vs-price-label">Hooptie's Price</div>
          <span className="vs-price">{displayPrice != null ? `$${displayPrice.toLocaleString()}` : '—'}</span>
        </div>

        <div className="vs-metrics">
          <div className="vs-metric">
            <span className="vs-metric-label">Mileage:</span>
            <span className="vs-metric-value">{displayMileage != null ? `${displayMileage.toLocaleString()} miles` : '—'}</span>
          </div>
          <div className="vs-metric">
            <span className="vs-metric-label">VIN:</span>
            <span className="vs-metric-value">{displayVin}</span>
          </div>
          {displayDom > 0 && (
            <div className="vs-metric-row">
              <span className="vs-dom-badge">
                <span className="vs-dom-check">✓</span>
                {displayDom} Days
              </span>
              <span className="vs-dom-avg">{avgDom} Avg</span>
            </div>
          )}
          {vehicle && (
            <div className="vs-metric-row">
              <span className="vs-dealer-badge">
                <span className="vs-dealer-check">✓</span>
                {vehicle.dealer || "Dealer"}
              </span>
              {vehicle.profit && (
                <>
                  <span className="vs-dealer-profit">${vehicle.profit.toLocaleString()}</span>
                  <span className="vs-dealer-emoji">😊</span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="vs-market-avg">
          <div className="vs-avg-item">
            <span className="vs-avg-label">Average Sold DOM:</span>
            <span className="vs-avg-value">{avgDom} days</span>
          </div>
          <div className="vs-avg-item">
            <span className="vs-avg-label">Avg Listing Price:</span>
            <span className="vs-avg-value">${avgPrice.toLocaleString()}</span>
          </div>
        </div>

        <div className="vs-buy-signals">
          <div className="vs-signals-header">Buy Signals</div>
          <div className="vs-signals-content">
            <div className="vs-signal-item">
              <span className="vs-signal-label">Active Price Band</span>
              <span className="vs-signal-value">{priceBand}</span>
            </div>
            <div className="vs-rec-bar">
              <div className="vs-rec-bar-fill"></div>
            </div>
          </div>
        </div>

        {soldDate && (
          <div className="vs-sold-info">
            <div className="vs-sold-item">
              <span className="vs-sold-label">DOM:</span>
              <span className="vs-sold-value">{displayDom} days</span>
            </div>
            <div className="vs-sold-item">
              <span className="vs-sold-label">Sold Date:</span>
              <span className="vs-sold-value">{formatSoldDate(soldDate)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

